import * as React from 'react';
import { Field } from 'redux-form';

import { WorkRequestDisposalMethod } from '@acceligentllc/shared/enums/workRequestDisposalMethod';

import { SegmentLabel } from '@acceligentllc/storybook';

import Checkbox from 'af-fields/Checkbox';
import Input from 'af-fields/Input';
import RadioGroup from 'af-fields/RadioGroup';

import JobContact from 'af-components/SharedForms/Contacts/ContactWrapper';

import JobUpsertFM from '../../formModel';
import { formSectionClass, formSectionClassColumn1, formSectionClassColumn2, formSectionClassColumn4, formSectionClassColumn8, formSectionClassRow } from '../../helpers';
import styles from '../../styles.module.scss';

const DISPOSAL_METHOD_OPTIONS = Object.keys(WorkRequestDisposalMethod).map((_dm) => {
	switch (_dm) {
		case (WorkRequestDisposalMethod.NONE): {
			return { value: _dm, label: 'None' };
		}

		case (WorkRequestDisposalMethod.OFF_SITE_DISPOSAL): {
			return { value: _dm, label: 'Off-site Disposal' };
		}

		case (WorkRequestDisposalMethod.ON_SITE_DISPOSAL): {
			return { value: _dm, label: 'On-site Disposal' };
		}

		case (WorkRequestDisposalMethod.THIRD_PARTY_TRANSPORT): {
			return { value: _dm, label: 'Third Party Transport' };
		}

		default: {
			return { value: _dm, label: 'None' };
		}
	}
});

type Props = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	change: (field: string, value: any) => void;
	formName: string;
	isDisabled: boolean;
};

const DisposalSection: React.FC<Props> = (props) => {
	const { change, formName, isDisabled } = props;

	const onDisposalMethodChange = React.useCallback((field: string, value) => {
		change(field, value);
	}, [change]);

	const ableToCleanAndWashEquipmentAtCustomerLocationStyle = React.useMemo(() => [formSectionClassColumn4, styles['job-form__tab-content__form-section__row__centered-column']].join(' '), []);
	const wasteGeneratedStyle = React.useMemo(() => [formSectionClassColumn1, styles['job-form__tab-content__form-section__row__centered-column']].join(' '), []);

	return (
		<div className={formSectionClass}>
			<SegmentLabel
				label="Disposal"
				tooltip="Waste management and disposal procedures associated with this job, including hazardous material handling, disposal methods, and designated contact information."
			/>
			<div className={formSectionClassRow}>
				<div className={ableToCleanAndWashEquipmentAtCustomerLocationStyle}>
					<Field
						component={Checkbox}
						id="ableToCleanAndWashEquipmentAtCustomerLocation"
						isDisabled={isDisabled}
						label="Able To Clean and Wash Equipment at Customer Location"
						name={JobUpsertFM.getAttributeName('ableToCleanAndWashEquipmentAtCustomerLocation')}
					/>
				</div>
				<div className={formSectionClassColumn2}>
					<Field
						component={Input}
						disabled={isDisabled}
						id="cleanAndWashLocation"
						label="Clean and Wash Location"
						name={JobUpsertFM.getAttributeName('cleanAndWashLocation')}
						placeholder="Enter Clean and Wash Location"
						type="text"
					/>
				</div>
			</div>
			<div className={formSectionClassRow}>
				<div className={formSectionClassColumn4}>
					Waste Generated:
				</div>
				<div className={formSectionClassColumn4}>
					Hazardous / Manifest
				</div>
			</div>
			<div className={[formSectionClassRow, styles['job-form__waste-types']].join(' ')}>
				<div className={wasteGeneratedStyle}>
					<Field
						component={Checkbox}
						id="isWasteGeneratedSolid"
						isDisabled={isDisabled}
						label="Solid"
						name={JobUpsertFM.getAttributeName('isWasteGeneratedSolid')}
					/>
				</div>
				<div className={wasteGeneratedStyle}>
					<Field
						component={Checkbox}
						id="isWasteGeneratedSludge"
						isDisabled={isDisabled}
						label="Sludge"
						name={JobUpsertFM.getAttributeName('isWasteGeneratedSludge')}
					/>
				</div>
				<div className={formSectionClassColumn2}>
					<Field
						component={Checkbox}
						id="isWasteGeneratedLiquid"
						isDisabled={isDisabled}
						label="Liquid"
						name={JobUpsertFM.getAttributeName('isWasteGeneratedLiquid')}
					/>
				</div>
				<div className={wasteGeneratedStyle}>
					<Field
						component={Checkbox}
						id="isWasteHazardous"
						isDisabled={isDisabled}
						label="Hazardous"
						name={JobUpsertFM.getAttributeName('isWasteHazardous')}
					/>
				</div>
				<div className={wasteGeneratedStyle}>
					<Field
						component={Checkbox}
						id="isWasteManifest"
						isDisabled={isDisabled}
						label="Manifest"
						name={JobUpsertFM.getAttributeName('isWasteManifest')}
					/>
				</div>
			</div>
			<div className={[formSectionClassRow, styles['job-form__waste-types']].join(' ')}>
				Disposal Method
				<div className={formSectionClassColumn8}>
					<RadioGroup
						changeField={onDisposalMethodChange}
						disabled={isDisabled}
						extraClass={styles['job-form__radio-group']}
						field={JobUpsertFM.getAttributeName('disposalMethod')}
						inline={true}
						items={DISPOSAL_METHOD_OPTIONS}
					/>
				</div>
			</div>
			<div className={formSectionClassRow}>
				<div className={formSectionClassColumn2}>
					<Field
						component={Input}
						disabled={isDisabled}
						id="wasteDescription"
						label="Waste Description"
						name={JobUpsertFM.getAttributeName('wasteDescription')}
						placeholder="Enter Waste Description"
						type="text"
					/>
				</div>
				<div className={formSectionClassColumn2}>
					<Field
						component={Input}
						disabled={isDisabled}
						id="expectedWasteQuantity"
						label="Expected Waste Quantity"
						name={JobUpsertFM.getAttributeName('expectedWasteQuantity')}
						placeholder="Enter Expected Waste Quantity"
						type="number"
					/>
				</div>
			</div>
			<div className={formSectionClassRow}>
				<div className={formSectionClassColumn2}>
					<Field
						component={Input}
						disabled={isDisabled}
						id="disposalInstructions"
						label="Disposal Instructions"
						name={JobUpsertFM.getAttributeName('disposalInstructions')}
						placeholder="Enter Disposal Instructions"
						type="text"
					/>
				</div>
			</div>
			<div className={formSectionClassRow}>
				<div className={formSectionClassColumn8}>
					Disposal Contact
					<JobContact
						className={styles['job-form__contact']}
						contactLabel="Enter Disposal Contact"
						disabled={isDisabled}
						fieldName={JobUpsertFM.getAttributeName('disposalContact')}
						formName={formName}
					/>
				</div>
			</div>
		</div>
	);
};

export default React.memo(DisposalSection);
